import { useState, useCallback, useRef, DragEvent } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { Upload, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { validateImageFile, getCroppedImage, getBase64Size } from '@/lib/imageUtils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';

type SponsorImageUploadProps = {
  /** Current image (Base64 data URL or empty) */
  value: string;
  /** Called with processed Base64 data URL, or empty string on remove */
  onChange: (base64: string) => void;
  /** Target width in pixels */
  width: number;
  /** Target height in pixels */
  height: number;
  /** Label shown above the upload area */
  label: string;
  /** Size hint text, e.g. "1200×675px (16:9)" */
  sizeHint?: string;
};

export default function SponsorImageUpload({
  value,
  onChange,
  width,
  height,
  label,
  sizeHint,
}: SponsorImageUploadProps) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputId = useRef(`sponsor-img-${Math.random().toString(36).slice(2, 8)}`).current;

  const aspect = width / height;

  const processFile = useCallback((file: File) => {
    const validation = validateImageFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target?.files?.[0];
    if (file) processFile(file);
    if (e.target) e.target.value = '';
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) processFile(file);
  };

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleUpload = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setUploading(true);
    try {
      const croppedBase64 = await getCroppedImage(imageSrc, croppedAreaPixels, width, height);

      if (getBase64Size(croppedBase64) > 5 * 1024 * 1024) {
        toast.error('Image is too large. Please choose a smaller image.');
        setUploading(false);
        return;
      }

      const res = await api.post<{ success: boolean; data: { imageBase64: string } }>(
        '/admin/process-image',
        { imageBase64: croppedBase64, width, height }
      );

      onChange(res.data.imageBase64);
      setImageSrc(null);
      setCroppedAreaPixels(null);
    } catch {
      toast.error('Failed to process image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    onChange('');
    setImageSrc(null);
  };

  const handleCancel = () => {
    setImageSrc(null);
    setCroppedAreaPixels(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  // Already have a processed image — show preview
  if (value && !imageSrc) {
    return (
      <div className="space-y-1.5">
        <Label className="text-[10px] text-muted-foreground block">{label}</Label>
        <div className="relative group">
          <img
            src={value}
            alt={label}
            className="w-full max-h-24 object-contain rounded border border-border bg-muted/30"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleRemove}
            className="absolute top-1 right-1 h-6 w-6 bg-black/50 text-white hover:bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity rounded-full"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
        {sizeHint && <p className="text-[10px] text-muted-foreground">{sizeHint}</p>}
      </div>
    );
  }

  // Cropping mode
  if (imageSrc) {
    return (
      <div className="space-y-2">
        <Label className="text-[10px] text-muted-foreground block">{label}</Label>
        <div className="relative h-40 bg-black rounded overflow-hidden">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] text-muted-foreground">Zoom</Label>
          <Slider
            value={[zoom]}
            min={1}
            max={3}
            step={0.1}
            onValueChange={(val) => { if (typeof val[0] === 'number') setZoom(val[0]); }}
          />
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={handleCancel} disabled={uploading} className="h-7 text-xs flex-1">
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={handleUpload} disabled={uploading} className="h-7 text-xs flex-1 gap-1">
            {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
            {uploading ? 'Processing...' : 'Apply'}
          </Button>
        </div>
      </div>
    );
  }

  // Upload dropzone
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] text-muted-foreground block">{label}</Label>
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`border border-dashed rounded p-3 text-center transition-colors cursor-pointer ${
          isDragging ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
        }`}
      >
        <Input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          onChange={handleFileSelect}
          className="hidden"
          id={inputId}
        />
        <Label htmlFor={inputId} className="cursor-pointer">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Upload className="h-4 w-4" />
            <span className="text-xs">Upload image</span>
          </div>
        </Label>
      </div>
      {sizeHint && <p className="text-[10px] text-muted-foreground">{sizeHint}</p>}
    </div>
  );
}
